import React, { useRef } from 'react';
import { Menu, X, ShieldCheck, Search } from 'lucide-react';
import { motion, useScroll } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { navItems, WHATSAPP_NUMBER } from './header/constants';
import { useHeaderScroll } from '../hooks/useHeaderScroll';
import { useRecentSearches } from '../hooks/useRecentSearches';
import { useSearchSuggestions } from '../hooks/useSearchSuggestions';
import { useHeaderNavigation } from '../hooks/useHeaderNavigation';
import DesktopNav from './header/DesktopNav';
import MobileMenu from './header/MobileMenu';
import SearchOverlay from './header/SearchOverlay';

export default function Header() {
  const location = useLocation();
  const currentPath = location.pathname;
  const headerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const mobileSearchRef = useRef<HTMLDivElement>(null);

  const isScrolled = useHeaderScroll();
  const { recentSearches, addSearch: handleSearchSubmit, clearSearches: clearRecentSearches } = useRecentSearches();
  const { 
    searchQuery, 
    setSearchQuery, 
    showSuggestions, 
    setShowSuggestions, 
    filteredSuggestions 
  } = useSearchSuggestions();

  const {
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    isMobileSearchFocused,
    setIsMobileSearchFocused,
    activeDropdown,
    setActiveDropdown,
    scrollToSection
  } = useHeaderNavigation({
    headerRef,
    searchRef,
    setShowSuggestions
  });

  const { scrollYProgress } = useScroll();

  const isHomePage = currentPath === '/';

  return (
    <header 
      ref={headerRef}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        (isScrolled || !isHomePage)
          ? 'bg-white/95 backdrop-blur-md py-3 shadow-md border-b border-slate-200' 
          : 'bg-transparent py-6'
      }`}
      dir="rtl"
    >
      {/* Scroll Progress Bar */}
      <motion.div
        className="absolute bottom-0 right-0 left-0 h-[2px] bg-brand-accent origin-right z-[70]"
        style={{ scaleX: scrollYProgress }}
      />
      <div className="container mx-auto px-4 flex items-center justify-between">
        {/* Logo */}
        <div className="flex-shrink-0">
          <a 
            href="#" 
            onClick={(e) => scrollToSection(e, '#')}
            aria-label="الحلول الرقمية لريادة الأعمال - العودة للرئيسية"
            className="block active:scale-95 transition-transform"
          >
            <img 
              src="https://blogger.googleusercontent.com/img/a/AVvXsEgk90zcNlep_PggBeutpAelZBCPIxHzO2NSOzQe9Mps-_8DjjQ2Uka89cam_ZKDmP_tv9UPcdrEu3KrcJtp5VFczXJhNruom741Knt2dtbFaAq1_-EAd8oTZqUgKSBa3ORoU3mvBT6ECZO7OiZbRcQmEcoS05PBRTMH1EZ7_eh7vkdR3HT1rmRnQlqOmiqx" 
              alt="الحلول الرقمية لريادة الأعمال" 
              width={180}
              height={64}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              referrerPolicy="strict-origin-when-cross-origin"
              onContextMenu={(e) => e.preventDefault()}
              draggable={false}
              className="h-12 md:h-16 w-auto brightness-110"
            />
          </a>
        </div>

        {/* Desktop Navigation */}
        <DesktopNav 
          navItems={navItems}
          currentPath={currentPath}
          activeDropdown={activeDropdown}
          setActiveDropdown={setActiveDropdown}
          scrollToSection={scrollToSection}
          WHATSAPP_NUMBER={WHATSAPP_NUMBER}
          searchRef={searchRef}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          showSuggestions={showSuggestions}
          setShowSuggestions={setShowSuggestions}
          handleSearchSubmit={handleSearchSubmit}
          recentSearches={recentSearches}
          filteredSuggestions={filteredSuggestions}
          clearRecentSearches={clearRecentSearches}
        />

        {/* Mobile Menu Toggle */}
        <div className="lg:hidden flex items-center gap-2">
          <button 
            onClick={() => setIsMobileSearchFocused(true)}
            className="text-brand-dark hover:text-brand-accent p-2.5 hover:bg-slate-100 active:scale-95 rounded-xl transition-all"
            aria-label="فتح البحث"
          >
            <Search size={22} />
          </button>
          <button 
            className="text-brand-dark p-2.5 hover:bg-slate-100 active:scale-95 rounded-xl transition-all"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "إغلاق القائمة" : "فتح القائمة"}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Search Overlay */}
      <SearchOverlay 
        isMobileSearchFocused={isMobileSearchFocused}
        setIsMobileSearchFocused={setIsMobileSearchFocused}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        handleSearchSubmit={handleSearchSubmit}
        recentSearches={recentSearches}
        clearRecentSearches={clearRecentSearches}
        filteredSuggestions={filteredSuggestions}
        scrollToSection={scrollToSection}
        setShowSuggestions={setShowSuggestions}
      />

      {/* Mobile Menu */}
      <MobileMenu 
        isMobileMenuOpen={isMobileMenuOpen}
        navItems={navItems}
        currentPath={currentPath}
        activeDropdown={activeDropdown}
        setActiveDropdown={setActiveDropdown}
        scrollToSection={scrollToSection}
        WHATSAPP_NUMBER={WHATSAPP_NUMBER}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        setIsMobileSearchFocused={setIsMobileSearchFocused}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
        mobileSearchRef={mobileSearchRef}
      />
    </header>
  );
}
